import React, { useState, useRef } from 'react';

// Main draggable list component
export function DraggableList({ items, onReorder, renderItem, getItemId }) {
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const dragItemRef = useRef(null);
  const dragNodeRef = useRef(null);

  const handleDragStart = (e, index) => {
    dragItemRef.current = index;
    dragNodeRef.current = e.currentTarget;
    e.currentTarget.style.opacity = '0.5';
    setDraggedIndex(index);
  };

  const handleDragEnter = (e, index) => {
    if (dragItemRef.current === index) return;
    setDragOverIndex(index);
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    
    const draggedIdx = dragItemRef.current;
    const targetIdx = dragOverIndex;

    if (targetIdx !== null && draggedIdx !== targetIdx) {
      const newItems = [...items];
      const draggedItem = newItems[draggedIdx];
      newItems.splice(draggedIdx, 1);
      newItems.splice(targetIdx, 0, draggedItem);
      console.log("newItems",newItems)
      onReorder(newItems);
    }

    setDraggedIndex(null);
    setDragOverIndex(null);
    dragItemRef.current = null;
    dragNodeRef.current = null;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // Rows can share an id (older template merges copied sub-document _ids). Suffix
  // the repeats so React keys stay unique — duplicate keys leave removed rows on screen.
  const seenIds = {};
  const itemKeys = items.map((item) => {
    const id = getItemId(item);
    seenIds[id] = (seenIds[id] || 0) + 1;
    return seenIds[id] > 1 ? `${id}-${seenIds[id]}` : id;
  });

  return (
    <div className="draggable-list">
      {items.map((item, index) => (
        <div key={itemKeys[index]} style={{ position: 'relative' }}>
          {draggedIndex !== null && dragOverIndex === index && (
            <div className="drop-indicator" />
          )}
          <div
            className={`sortable-item ${draggedIndex === index ? 'dragging' : ''}`}
            draggable={false}
            onDragOver={handleDragOver}
            onDragEnter={(e) => handleDragEnter(e, index)}
          >
            <div
              className="drag-handle"
              draggable={true}
              onDragStart={(e) => handleDragStart(e, index)}
              onDragEnd={handleDragEnd}
            >
              ⋮⋮
            </div>
            <div className="item-content">
              {renderItem(item, index)}
            </div>
          </div>
        </div>
      ))}

      <style>{`
        .draggable-list {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        
        .sortable-item {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .sortable-item.dragging {
          opacity: 0.5;
        }

        .drop-indicator {
          position: absolute;
          top: -2px;
          left: 0;
          right: 0;
          height: 2px;
          background: #12AB81;
          border-radius: 3px;
          z-index: 10;
          animation: pulse 0.5s ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            transform: scaleY(1);
          }
          50% {
            opacity: 0.7;
            transform: scaleY(1.5);
          }
        }
        
        .drag-handle {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 15px;
          margin-left:5px;
          height: 20px;
          cursor: grab;
          color: #12AB81;
          font-weight:900;
          font-size: 18px;
          user-select: none;
          flex-shrink: 0;
        }
        
        .drag-handle:active {
          cursor: grabbing;
        }
        
        .item-content {
          flex: 1;
        }
      `}</style>
    </div>
  );
}

// Demo Usage
export default function Demo() {
  const [tasks, setTasks] = useState([
    { id: '1', title: 'Buy groceries', completed: false },
    { id: '2', title: 'Walk the dog', completed: true },
    { id: '3', title: 'Write code', completed: false },
    { id: '4', title: 'Read a book', completed: false },
  ]);

  const handleToggle = (id) => {
    setTasks(tasks.map(task => 
      task.id === id ? { ...task, completed: !task.completed } : task
    ));
  };

  const handleDelete = (id) => {
    setTasks(tasks.filter(task => task.id !== id));
  };

  return (
    <div style={{ maxWidth: '600px', margin: '40px auto', fontFamily: 'system-ui' }}>
      {/* <h2>My Tasks - Drag to Reorder</h2>
      <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '20px' }}>
        Grab the ⋮⋮ handle to drag and reorder items
      </p> */}
      
      <DraggableList
        items={tasks}
        onReorder={setTasks}
        getItemId={(item) => item.id}
        renderItem={(task) => (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px',
            background: 'white',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
          }}>
            <input
              type="checkbox"
              checked={task.completed}
              onChange={() => handleToggle(task.id)}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <span style={{
              flex: 1,
              textDecoration: task.completed ? 'line-through' : 'none',
              color: task.completed ? '#9ca3af' : '#000',
            }}>
              {task.title}
            </span>
            <button
              onClick={() => handleDelete(task.id)}
              style={{
                padding: '6px 12px',
                background: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              Delete
            </button>
          </div>
        )}
      />
    </div>
  );
}